import * as React from 'react'
import Dropzone from 'react-dropzone'
import styled from 'styled-components'
import { websiteCastingWidgetPluginRemoveAttachment, websiteCastingWidgetPluginUploadAttachment } from '../../../../services/models/website-casting-widgets.service'
import { StoreProps } from '../../../../services/stores/casting.store'
import { I18nService } from '../../../../services/utils/i18n.service'
import { InfoBoxError } from '../casting-styled.component'

const DropzoneContainer = styled.div`
  border: 2px dashed ${ props => props.theme.colors.clr_0 };
  transition: border-color 0.2s ease-in-out;
  border-radius: 0.25em;
  padding: 1em;
  margin-top: 1em;

  &:hover {
    border-color: ${ props => props.theme.colors.clr_1 };
  }
`

const i18nScoped = new I18nService(require('./casting-attachments-dropzone.component.yml'))

export default function CastingAttachmentsDropzoneComponent ({ store, data: { market, offeredService, attachments } }: StoreProps) {
  const uploadFile = React.useCallback((file: File) => {
    // use store.data to always track the most up-to-date reference
    if (['pdf', 'png', 'jpg', 'jpeg'].indexOf(file.name.split('.').pop().toLowerCase()) === -1) {
      store.set({ attachments: store.data.attachments.concat({ filename: file.name, state: 'error', reason: i18nScoped.t('attachments_invalid_extension') }) })
      return Promise.resolve()
    }

    const newAttachment: StoreProps['data']['attachments'][0] = { filename: file.name, state: 'loading' }
    store.set({ attachments: store.data.attachments.concat(newAttachment) })
    return websiteCastingWidgetPluginUploadAttachment(market, file)
      .then(({ uuid }) => store.set({ attachments: store.data.attachments.map(_ => _ === newAttachment ? { ...newAttachment, state: 'success', uuid } : _) }))
      .catch(() => store.set({ attachments: store.data.attachments.map(_ => _ === newAttachment ? { ...newAttachment, state: 'error' } : _) }))
  }, [market, store])

  const uploadFiles = React.useCallback((acceptedFiles: File[]) => {
    return Promise.all(acceptedFiles.slice(0, offeredService.attachments_max_count - attachments.filter(_ => _.state !== 'error').length).map(uploadFile))
  }, [attachments, offeredService.attachments_max_count, uploadFile])

  const removeFile = React.useCallback(async (attachment: StoreProps['data']['attachments'][0]) => {
    if (attachment.uuid) await websiteCastingWidgetPluginRemoveAttachment(market, attachment.uuid)
    store.set({ attachments: attachments.filter(_ => _ !== attachment) })
  }, [attachments, market, store])

  const renderUploadForm = React.useCallback(() => {
    const countAllowed = offeredService.attachments_max_count - attachments.filter(_ => _.state !== 'error').length

    return <div id="js-test-attachments-form">
      { attachments.length > 0 && <div style={{ margin: '1em 0' }}>
        { attachments.map((attachment, index) => <div key={ index }>
          { attachment.state === 'loading' && <div className="iconLoading-container" style={{ display: 'flex', alignItems: 'center' }}>
            <div className="iconLoading"></div>
            <div className="iconLoading-message">{ attachment.filename }</div>
          </div> }

          { attachment.state === 'success' && <div>
            <a className="icon-close" onClick={ () => removeFile(attachment) } style={{ padding: '0.4em 0.8em' }}></a>
            { attachment.filename }
          </div> }

          { attachment.state === 'error' && <div>
            <a className="icon-close" onClick={ () => removeFile(attachment) } style={{ padding: '0.4em 0.8em' }}></a>
            <span style={{ textDecoration: 'line-through' }}>{ attachment.filename }</span>
            <span className="input-message input-message--error u-mls">{ attachment.reason }</span>
          </div> }
        </div>) }
      </div> }

      <Dropzone onDrop={ uploadFiles }>
        { ({ getRootProps, getInputProps, isDragActive }) => (
          <DropzoneContainer { ...getRootProps() }>
            <input id="js-test-attachment-input" { ...getInputProps() } disabled={ countAllowed === 0 }/>
            { countAllowed ? <p>{ i18nScoped.t(isDragActive ? 'drop_here' : 'drag_or_click') }</p> : null }
            <p>{ i18nScoped.t('yet_n_files_allowed', { count: countAllowed }) }</p>
          </DropzoneContainer>
        ) }
      </Dropzone>
    </div>
  }, [attachments, offeredService.attachments_max_count, removeFile, uploadFiles])

  return <div>
    { offeredService.attachments_description ? <div dangerouslySetInnerHTML={{ __html: offeredService.attachments_description }} /> : null }
    { !store.isEditingCasting() && renderUploadForm() }
    { store.isEditingCasting() && <InfoBoxError>{ i18nScoped.t('attachments_edit_not_allowed') }</InfoBoxError> }
  </div>
}
